<template>
  <div ref="group" class="sp-otp-input" :class="classModifiers" role="group" :aria-label="groupLabel">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      class="sp-otp-input__digit"
      type="text"
      inputmode="numeric"
      :autocomplete="autocompleteFor(index)"
      :data-index="index"
      :data-testid="digitTestId"
      :aria-label="digitLabel(index)"
      :value="digit"
      :disabled="disabled"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, toRef, useAttrs, useTemplateRef } from "vue";

import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { formAssociatedHost } from "#ce/framework/utils";
import { t } from "#ce/shared/forms-i18n";
import { useOtpDigits } from "./composables/useOtpDigits.js";
import { useOtpNavigation } from "./composables/useOtpNavigation.js";
import { useOtpPaste } from "./composables/useOtpPaste.js";
import { useOtpUserEdits } from "./composables/useOtpUserEdits.js";
import { props as propsDefinitions } from "./props.js";

// Focus and `<label for>` clicks reach the first digit box through `delegatesFocus`.
defineOptions(formAssociatedHost({ inheritAttrs: false, shadowRootOptions: { delegatesFocus: true } }));

const emit = defineEmits(["complete"]);

const props = defineProps(propsDefinitions);
const attrs = useAttrs();

const { internals, mirrorFormValue, onFormReset } = useForm();
const { exposeProperties } = useExpose();
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const group = useTemplateRef("group");

const {
  count,
  digits,
  value,
  defaultValue,
  parseValue,
  setDigit,
  writeUserValue,
  writeValue,
  adoptDefault,
  restoreDefault,
} = useOtpDigits({ count: toRef(props, "count") });

const { boxIndex, focusAfterTyping } = useOtpNavigation({ group, digits, autofocus: () => attrs.autofocus });

const { reportUserEdit, resetEditTracking } = useOtpUserEdits({
  group,
  count,
  digits,
  value,
  setDigit,
  parseValue,
  writeUserValue,
  boxIndex,
  focusAfterTyping,
  emitComplete: (completed) => emit("complete", completed),
});
const { pasteFailed } = useOtpPaste({ group, value, parseValue, writeUserValue, reportUserEdit });

mirrorFormValue(value);
onFormReset(handleFormReset);

exposeProperties({
  value: { get: () => value.value, set: writeValue },
  defaultValue: { get: () => defaultValue.value, set: adoptDefault },
});

const labelElements = ref([]);
const { ariaLabel: groupLabel } = useAriaLabel(group, {
  label: () => attrs.ariaLabel,
  labelledby: toRef(props, "ariaLabelledby"),
  elements: labelElements,
});

const classModifiers = computed(() => ({ "--paste-failed": pasteFailed.value }));

function readLabels() {
  labelElements.value = [...(internals?.labels ?? [])];
}

function handleFormReset() {
  restoreDefault();
  pasteFailed.value = false;
  resetEditTracking();
}

function autocompleteFor(index) {
  return index === 0 ? "one-time-code" : "off";
}

function digitLabel(index) {
  return t("otp_digit_label", { index: index + 1, count: count.value });
}

onMounted(readLabels);
</script>

<style lang="scss">
:host {
  display: block;
}
:host(:focus) {
  outline: none;
  box-shadow: none;
}
</style>

<style lang="scss" scoped>
.sp-otp-input {
  --gap: var(--sp-comp-otp-input-gap, 0.75ch);
  --border-width: var(--sp-comp-otp-input-border-width, var(--sp-sys-border-width-default));
  --border-color: var(--sp-comp-otp-input-border-color, var(--sp-sys-form-field-border-color));
  --border-color-hover: var(--sp-comp-otp-input-border-color-hover, var(--border-color));
  --invalid-border-color: var(--sp-comp-otp-input-invalid-border-color, var(--sp-sys-form-field-invalid-border-color));

  display: flex;
  align-items: center;
  justify-content: center;

  &__digit {
    border: var(--border-width) solid var(--border-color);
    padding: var(--sp-comp-otp-input-padding, 0);
    border-radius: var(--sp-comp-otp-input-border-radius, var(--sp-sys-shape-control));
    font-weight: var(--sp-comp-otp-input-font-weight, normal);
    font-size: var(--sp-comp-otp-input-font-size, inherit);
    text-align: center;
    min-inline-size: 3.3ch;
    min-height: 4.1ex;
    width: 1ch;
    box-shadow: none;
    transition: box-shadow 0.15s ease-in-out;

    & + & {
      margin-left: var(--gap);
    }

    &:hover {
      border-color: var(--border-color-hover);
    }

    &:focus {
      box-shadow: var(--sp-comp-otp-input-focus-box-shadow, var(--sp-sys-form-field-focus-box-shadow));
    }

    &:focus,
    &:focus-visible {
      outline: none;
    }
  }

  &.--paste-failed &__digit {
    animation: shaking 0.85s;
    animation-iteration-count: 1;
    animation-delay: 0.3s;
    box-shadow: var(--sp-comp-otp-input-invalid-box-shadow, var(--sp-sys-form-field-invalid-box-shadow));
    border-color: var(--invalid-border-color);
  }
}

@keyframes shaking {
  0%,
  68% {
    transform: rotate(0deg);
  }
  8%,
  76% {
    transform: rotate(5deg);
  }
  16%,
  84% {
    transform: rotate(0deg);
  }
  24%,
  92% {
    transform: rotate(-5deg);
  }
  32%,
  100% {
    transform: rotate(0deg);
  }
}
</style>
